import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/motion";
import { DistributorForm } from "@/components/forms";
import { Layers, Package, Briefcase, MapPin, ClipboardList, PhoneCall, Handshake, Truck } from "lucide-react";

const benefits = [
  { icon: Layers, title: "Product Portfolio", text: "Access to ICROPSIA maize and wheat seed varieties." },
  { icon: Package, title: "Professional Packaging", text: "Retail-ready branded packaging with full label information." },
  { icon: Briefcase, title: "Business Partnership", text: "Build a long-term relationship with a growing agricultural brand." },
  { icon: MapPin, title: "Regional Opportunity", text: "Serve farmers in Bihar through a focused agricultural product portfolio." },
];

const steps = [
  { icon: ClipboardList, title: "Submit Enquiry", text: "Fill the partnership form below with your business details." },
  { icon: PhoneCall, title: "Discussion", text: "Our team connects with you to understand your market and requirement." },
  { icon: Handshake, title: "Onboarding", text: "We finalize the partnership and share product and supply information." },
  { icon: Truck, title: "Supply Begins", text: "Receive professional 25 kg branded packs for your market." },
];

export default function Partner() {
  return (
    <>
      <Seo
        title="Become a Distributor — Partner With ICROPSIA India"
        description="Retailers, dealers, distributors and agri-businesses in Bihar can apply to stock ICROPSIA maize and wheat seeds in retail-ready 25 kg packaging."
      />
      <section className="pt-40 pb-16 bg-brand-ink relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="container-site relative max-w-3xl">
          <Reveal>
            <Eyebrow light>Partnership</Eyebrow>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Grow Your Agricultural Business With <span className="font-editorial italic font-semibold text-brand-wheat">ICROPSIA</span>
            </h1>
            <p className="mt-5 text-white/70 leading-relaxed">
              We work with agricultural distributors, seed dealers, retailers, and local agri-businesses looking to add
              quality-focused seed products to their portfolio.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-site grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {benefits.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-brand-deep/10 bg-white p-6 hover:border-brand-gold/50 hover:shadow-lg transition-all">
                <span className="w-10 h-10 rounded-xl bg-brand-deep flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-brand-wheat" />
                </span>
                <h2 className="font-display font-bold text-brand-deep">{title}</h2>
                <p className="mt-1.5 text-sm text-brand-charcoal/60 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="container-site">
          <Reveal className="max-w-xl">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-brand-deep">From Enquiry to Supply</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="relative rounded-2xl bg-brand-beige/70 border border-brand-deep/10 p-6">
                  <span className="absolute top-5 right-5 font-display text-4xl font-extrabold text-brand-gold/30">0{i + 1}</span>
                  <Icon className="w-6 h-6 text-brand-deep mb-4" />
                  <h3 className="font-display font-bold text-brand-deep text-sm">{title}</h3>
                  <p className="mt-1.5 text-sm text-brand-charcoal/60 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-site">
          <Reveal>
            <div className="grid lg:grid-cols-5 gap-10 items-start rounded-[2rem] bg-brand-beige/60 border border-brand-deep/10 p-7 sm:p-12">
              <div className="lg:col-span-2">
                <Eyebrow>Apply Now</Eyebrow>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-brand-deep">
                  Interested in Stocking ICROPSIA Seeds?
                </h2>
                <p className="mt-4 text-brand-charcoal/65 leading-relaxed text-sm sm:text-base">
                  Share your business details and our team will review your enquiry and contact you shortly with
                  partnership information.
                </p>
              </div>
              <div className="lg:col-span-3 bg-white rounded-3xl border border-brand-deep/10 p-6 sm:p-8 shadow-xl shadow-brand-deep/5">
                <DistributorForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
